/**
 * safeNextPath — validate a `?next=` redirect target.
 *
 * Auth flows bounce users to `next` after signing in. If that value is taken
 * straight from the query string, an attacker can send
 *   /auth/login?next=https://evil.example/login
 * and the victim lands on a convincing look-alike immediately after
 * authenticating on the real site — a classic credential-phishing chain that
 * borrows our domain's trust.
 *
 * Only same-site ABSOLUTE PATHS are allowed. Everything else falls back.
 * Rejected: absolute URLs (https://…), protocol-relative (//evil.com),
 * backslash tricks (/\evil.com), and anything not starting with "/".
 */
export function safeNextPath(next: string | null | undefined, fallback = "/dashboard"): string {
  if (!next) return fallback
  let value = next.trim()
  if (!value) return fallback

  // A browser may hand us an already-decoded value; decode once more so that
  // an encoded "//evil.com" can't slip past the checks below.
  try { value = decodeURIComponent(value) } catch { /* keep raw on bad escapes */ }

  // Must be a rooted path, and must not be protocol-relative or scheme-bearing.
  if (!value.startsWith("/")) return fallback
  if (value.startsWith("//") || value.startsWith("/\\")) return fallback
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return fallback   // javascript:, data:, https:
  if (value.includes("\\")) return fallback                  // \ is / on some parsers

  return value
}
